// Mobile parity: on mount, write onboardingComplete:true to users/{uid} and
// write proxy_mailbox/{localpart} with {email, userId}. Stream a 5-line log,
// enable the final CTA when both save + log stream complete.
import { useEffect, useRef, useState } from "react"
import { useOnboarding } from "../../contexts/OnboardingContext"
import {
  ContinueButton,
  OB_COLORS as C,
  OB_FF as FF,
} from "./OnboardingStepShell"

export default function WelcomeCompleteStep({ onComplete }) {
  const { profile, completeOnboarding } = useOnboarding()
  const [saveDone, setSaveDone] = useState(false)
  const [logs, setLogs]         = useState([])
  const [logsDone, setLogsDone] = useState(false)
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return
    ran.current = true

    const firstName = (profile?.name || profile?.firstName || "").split(" ")[0] || "you"
    const lines = [
      { message: `Initializing profile for ${firstName}…`, done: false },
      { message: "796+ career portals indexed",            done: false },
      { message: "1,084 roles matched to your profile",    done: false },
      { message: "Preferences mapped — monitoring active", done: false },
      { message: "Ready",                                   done: true  },
    ]

    const timers = []
    lines.forEach((line, i) => {
      const delay = 480 + i * 180
      timers.push(setTimeout(() => {
        setLogs((prev) => [...prev, line])
        if (i === lines.length - 1) setLogsDone(true)
      }, delay))
    })

    completeOnboarding()
      .then(() => setSaveDone(true))
      .catch(() => setSaveDone(true))

    return () => timers.forEach(clearTimeout)
  }, [])

  const ready = saveDone && logsDone

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      width: "100%", maxWidth: 480,
      fontFamily: FF, color: C.text,
    }}>
      <style>{`
        @keyframes wc-pulse { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.5;transform:scale(1.3);} }
        @keyframes wc-fade  { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
        .wc-row   { animation: wc-fade 320ms cubic-bezier(.2,0,0,1) both; }
        .wc-pulse { animation: wc-pulse 1.1s ease-in-out infinite; transform-origin: center; }
        @keyframes wc-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Badge */}
      <div style={{
        display: "inline-flex", alignSelf: "flex-start",
        padding: "4px 11px",
        background: "#FFF5EC", border: "1px solid #F7D9BE",
        borderRadius: 100,
        fontSize: 11, fontWeight: 700, color: C.ember,
        letterSpacing: "0.04em", marginBottom: 14,
      }}>
        You're all set
      </div>

      <h1 style={{
        margin: 0, fontSize: 36, lineHeight: 1.1,
        fontWeight: 700, letterSpacing: "-0.03em",
      }}>
        Your agent{"\n"}is live.
      </h1>
      <p style={{ margin: "8px 0 0", fontSize: 14, color: C.textSec, lineHeight: 1.5 }}>
        Setting up your profile and scanning opportunities.
      </p>

      <div style={{ marginTop: 32, marginBottom: 24 }}>
        {logs.map((line, i) => {
          const isLast = i === logs.length - 1
          const isActive = !logsDone && isLast
          return (
            <div key={i} className="wc-row" style={{
              display: "flex", gap: 14, alignItems: "flex-start",
            }}>
              {/* Dot + connector */}
              <div style={{ width: 22, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ marginTop: 2 }}>
                  {line.done ? (
                    <div style={{
                      width: 18, height: 18, borderRadius: "50%",
                      background: "rgba(58,104,69,0.15)",
                      border: `1.5px solid #3A6845`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
                        <path d="M4 10l5 5 7-7" stroke="#3A6845" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  ) : isActive ? (
                    <div className="wc-pulse" style={{
                      width: 18, height: 18, borderRadius: "50%",
                      background: "rgba(232,112,42,0.12)",
                      border: `1.5px solid ${C.ember}`,
                    }} />
                  ) : (
                    <div style={{
                      width: 18, height: 18, borderRadius: "50%",
                      border: `1.5px solid ${C.border}`,
                    }} />
                  )}
                </div>
                {(!isLast || isActive) && (
                  <div style={{
                    width: 1.5, height: 30, marginTop: 4,
                    background: line.done ? "rgba(58,104,69,0.3)" : C.border,
                  }} />
                )}
              </div>

              <div style={{ flex: 1, paddingBottom: 16 }}>
                <div style={{
                  fontSize: 14, lineHeight: 1.4,
                  color: line.done || isActive ? C.text : C.textSec,
                  fontWeight: line.done ? 600 : 400,
                }}>
                  {line.message}
                </div>
                {isActive && (
                  <div className="wc-pulse" style={{
                    marginTop: 2, fontSize: 11, color: C.textMuted,
                  }}>
                    Processing…
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <ContinueButton
        label="Start Exploring"
        onClick={onComplete}
        disabled={!ready}
        loading={!ready}
      />
    </div>
  )
}
